import { useState } from 'react';
import { Alert, Inline, Link, t, toast } from '@kanbios/ui';
import { api, ApiFailure, unwrap } from '../lib/api';
import { PendingButton } from './PendingButton';

type Open = { repository: string; number: number; url: string };

/** The pull request a 409 « déjà ouverte » names, when the backend gives it. */
export function openPullRequestOf(failure: ApiFailure | null): Open | null {
  const pr = failure?.status === 409 ? (failure.body.details?.pullRequest as Open | undefined) : undefined;
  return pr?.repository && pr.number ? pr : null;
}

/**
 * États transverses « Modification en attente »: a change already proposed is
 * open in GitHub. Its page, or close it here (with the pull requests opened in
 * the same request) and propose again, without going to GitHub.
 */
export function PullRequestConflict({ failure, onClosed }: { failure: ApiFailure; onClosed: () => void }) {
  const pr = openPullRequestOf(failure)!;
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const close = async () => {
    setClosing(true);
    try {
      const [owner, repo] = pr.repository.split('/');
      const { closed } = unwrap(await api.maintenance.POST('/pull-requests/{owner}/{repo}/{number}/close', { params: { path: { owner: owner!, repo: repo!, number: pr.number } } }));
      toast({ tone: 'pos', title: t('pullRequest.closed', { n: closed.length, numbers: closed.map(c => `#${c.number}`).join(', ') }) });
      onClosed();
    } catch (e) {
      setError(e instanceof ApiFailure ? e : null);
    } finally {
      setClosing(false);
    }
  };
  return (
    <Alert tone="warn" title={(error ?? failure).body.message}>
      <Inline gap={16} align="center" wrap>
        <span>{error ? error.body.hint : t('pullRequest.conflictHint')}</span>
        <Link href={pr.url} external>{t('pullRequest.link', { number: pr.number })}</Link>
        <PendingButton variant="secondary" size="sm" icon="close" pending={closing} onClick={() => void close()}>{t('pullRequest.close', { number: pr.number })}</PendingButton>
      </Inline>
    </Alert>
  );
}
